import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';

import { BasketActions, BasketActionTypes, BasketLoadingItem, LoadingItemTypes } from '../basket.types';
import { addProductSaga } from './addProduct';
import { AppState, ErrorAction } from '../../root.types';
import { BasketItemModel, BasketDetailsModel } from 'models/Basket';
import { api } from 'repositories/api';
import { userHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { initBasketPageSaga } from './initBasketPage';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

export interface CheckSofaglideForUpdateActionStart extends Action<BasketActionTypes.CHECK_SOFAGLIDE_FOR_UPDATE_START> {
}

export interface CheckSofaglideForUpdateActionSuccess extends Action<BasketActionTypes.CHECK_SOFAGLIDE_FOR_UPDATE_SUCCESS> {
}

export interface CheckSofaglideForUpdateActionError extends ErrorAction<BasketActionTypes.CHECK_SOFAGLIDE_FOR_UPDATE_ERROR> {
}

export interface UpdateSofaglideAction extends Action<BasketActionTypes.UPDATE_SOFAGLIDE> {
}

export interface UpdateSofaglideActionStart extends Action<BasketActionTypes.UPDATE_SOFAGLIDE_START> {
    loadingItem: BasketLoadingItem;
    quantity: number;
}

export interface UpdateSofaglideActionSuccess extends Action<BasketActionTypes.UPDATE_SOFAGLIDE_SUCCESS> {
    loadingItem: BasketLoadingItem;
    quantity: number;
}

export interface UpdateSofaglideActionError extends ErrorAction<BasketActionTypes.UPDATE_SOFAGLIDE_ERROR> {
    loadingItem: BasketLoadingItem;
    quantity: number;
}

/** Update Sofaglide quantity only for one change if it needs (works as a recursion function) */
export function* updateSofaglideSaga(): BasketSagaReturnType {
    yield put<BasketActions>({
        type: BasketActionTypes.CHECK_SOFAGLIDE_FOR_UPDATE_START
    });

    try {
        let state: AppState = yield select();
        const entryItemForUpdate = state.basketState.basket!.entries?.find((entry: BasketItemModel) => entry.shouldUpdateLinkedSofaglide());

        if (entryItemForUpdate) {
            const itemProductCode = entryItemForUpdate.linkedSofaglide!.baseProductCode;
            const quantity = entryItemForUpdate.quantity;

            yield callBasketSaga(updateSofaglideQueueSaga, {
                type: BasketActionTypes.UPDATE_SOFAGLIDE_START,
                loadingItem: { id: itemProductCode, loadingType: LoadingItemTypes.ChangeSofaglideQuantity },
                quantity
            });
        }

        yield put<BasketActions>({
            type: BasketActionTypes.CHECK_SOFAGLIDE_FOR_UPDATE_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.CHECK_SOFAGLIDE_FOR_UPDATE_ERROR,
            error: errorToBasketErrorModel(error, 'updateSofaglideSaga')
        });

        return false;
    }
}

function* updateSofaglideQueueSaga({ loadingItem, quantity }: UpdateSofaglideActionStart): BasketSagaReturnType {
    yield put<BasketActions>({
        type: BasketActionTypes.UPDATE_SOFAGLIDE_START,
        loadingItem,
        quantity
    });
    try {
        let state: AppState = yield select();
        const basketId = state.basketState.basket!.guid;
        const basket = state.basketState.basket!;
        let entryItem = getEntryItem(basket, loadingItem);

        if (entryItem) {
            yield api.hybris.removeBasketItem(
                userHelper.getCurrentUserName(),
                basketId,
                entryItem.linkedSofaglide!.entryNumber,
                loadingItem.id
            );

            const sofaglide = basket.entries.find((entry: BasketItemModel) => entry.entryNumber === entryItem!.linkedSofaglide!.entryNumber)!;
            const prevSofaglideQuantity = entryItem.linkedSofaglide!.quantity;

            /** code means linked sofa sku (entry.product.code) */
            const linkedSofaCode = entryItem.availableSofaglides[0]!.crossSellingProductCode;

            if (linkedSofaCode) {
                yield callBasketSaga(addProductSaga, {
                    type: BasketActionTypes.ADD_PRODUCT,
                    code: linkedSofaCode,
                    quantity,
                    sofaglideParentCode: loadingItem.id
                });
            }

            yield callBasketSaga(initBasketPageSaga, {
                type: BasketActionTypes.INIT_BASKET_PAGE
            });

            yield sendAnalytics(sofaglide, prevSofaglideQuantity, loadingItem);

            yield put<BasketActions>({
                type: BasketActionTypes.UPDATE_SOFAGLIDE_SUCCESS,
                loadingItem,
                quantity
            });
        }

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.UPDATE_SOFAGLIDE_ERROR,
            loadingItem,
            quantity,
            error: errorToBasketErrorModel(error, 'updateSofaglideQueueSaga')
        });

        return false;
    }
}

function getEntryItem(basket: BasketDetailsModel, loadingItem: BasketLoadingItem): BasketItemModel | undefined {
    return basket.entries?.find((entry: BasketItemModel) => {
        return entry?.availableSofaglides[0]?.baseProductCode === loadingItem.id;
    });
}

function* sendAnalytics(item: BasketItemModel, prevQuantity: number, loadingItem: BasketLoadingItem): Generator<any, void, any> {
    yield analyticsHelper.withLoggingGenerator(function* () {
        const state: AppState = yield select();
        const updatedBasket = state.basketState.basket!;
        const updatedEntryItem = getEntryItem(state.basketState.basket!, loadingItem);
        const updatedSofaglideQuantity = updatedEntryItem?.linkedSofaglide?.quantity || 0;

        const itemsCountDifference = updatedSofaglideQuantity - prevQuantity;

        if (itemsCountDifference === 0) {
            return;
        }

        if (itemsCountDifference > 0) {
            api.google.addSofaglideProduct(item.product.code, Math.abs(itemsCountDifference), item.product.categories[0]?.code, item.basePrice);
            api.exponea.addToCart(updatedBasket, item, Math.abs(itemsCountDifference));
        } else {
            api.google.removeSofaglideProduct(item.product.code, Math.abs(itemsCountDifference), item.product.categories[0]?.code, item.basePrice);
            api.exponea.removeFromCart(updatedBasket, item, Math.abs(itemsCountDifference));
        }
    }, 'updateSofaglideQueueSaga');
}
